<template>
  <CustomTable ref="table" :elements="elements" :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <div class="association-filters">
        <button class="association-filter-button" :class="{ active: isAssociationFilterActive(true) }" title="Afficher les clients associés" @click="setAssociationFilter(true)">
          <span class="fas fa-link"></span>Associés
        </button>

        <button class="association-filter-button" :class="{ active: isAssociationFilterActive(false) }" title="Afficher les clients non associés" @click="setAssociationFilter(false)">
          <span class="fas fa-link-slash"></span>Non associés
        </button>

        <button class="association-filter-button" :class="{ active: isAssociationFilterActive(null) }" title="Afficher tous les clients" @click="setAssociationFilter(null)">
          <span class="fas fa-list"></span>Tous
        </button>
      </div>
    </template>
    <template v-slot:table>
      <thead v-if="$refs.table">
      <tr>
        <custom-header field="name">Client</custom-header>
        <custom-header field="siret">SIRET</custom-header>
        <custom-header field="directory">Répertoire français</custom-header>
        <custom-header field="action">Action</custom-header>
      </tr>
      </thead>
      <tbody v-if="data.length">
      <tr v-for="client in $refs.table.$refs.searchArray.simpleFilter(data)" :key="client.id">
        <custom-body field="name" class="truncate-cell" :title="client.name"><b>{{client.name}}</b></custom-body>
        <custom-body field="siret">{{client.siret || "-"}}</custom-body>
        <custom-body field="directory">
          <div v-if="client.frenchDirectory?.company" class="directory associated">
            <div class="title" :title="client.frenchDirectory.company.formal_name">✔ {{client.frenchDirectory.company.formal_name}}</div>
            <div class="subtitle">SIRET : {{client.frenchDirectory.siret || "-"}}</div>
            <div class="identifier">{{client.frenchDirectory.identifier}}</div>
          </div>
          <div v-else-if="client.frenchDirectory && client.type === 'INDIVIDUAL'" class="directory explicit-association">
            <div class="title">Particulier</div>
            <div class="subtitle">Aucune entrée dans le Répertoire français</div>
          </div>
          <div v-else-if="client.frenchDirectory && client.type === 'INTERNATIONAL_COMPANY'" class="directory explicit-association">
            <div class="title">Entreprise internationale</div>
            <div class="subtitle">Aucune entrée dans le Répertoire français</div>
          </div>
          <div v-else class="directory not-associated">
            ⚠ Non associé
          </div>
        </custom-body>
        <custom-body>
          <button @click="$refs.manageClientDictionaryRelation.openModal(client)" class="button-icon"><span class="fas fa-rotate"></span></button>
          <button v-if="client.frenchDirectory" @click="removeAssociation(client)" class="button-icon"><span class="fas fa-trash"></span></button>
        </custom-body>
      </tr>
      </tbody>
      <tbody v-else>
      <tr>
        <td colspan="4" class="empty">Aucun client</td>
      </tr>
      </tbody>
    </template>
  </CustomTable>
  <ClientDictionaryAssociationModal ref="manageClientDictionaryRelation" :callback="loadData"/>
</template>

<script>
import CustomTable from "@/components/CustomTable.vue";
import { library } from '@fortawesome/fontawesome-svg-core';
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import ClientDictionaryAssociationModal from "@/invoice-transmission/components/modals/ClientDictionaryAssociationModal.vue";
import { FrenchDirectoryCacheController } from "@/invoice-transmission/controllers/invoice_transmission/FrenchDirectoryCacheController";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {CustomTableService} from "@/services/CustomTableService";
import { faRotate, faTrash, faLink, faLinkSlash, faList } from "@fortawesome/free-solid-svg-icons";
import {CLIENT_STATUS_ENUM} from "@/models/clientStatusEnum";
library.add(faRotate, faTrash, faLink, faLinkSlash, faList);

export default {
  name: 'ClientDictionaryTable',
  components: {
    ClientDictionaryAssociationModal,
    CustomBody,
    CustomHeader,
    CustomTable
  },
  data: function () {
    return {
      data: [],
      elements: [
        {label: "Siret", value: "siret", type: "text"},
        {label: "Nom", value: "name", type: "text"},
        {label: "Status", value: "status", type: "text"},
        {label: "Associé", value: "associated", type: "boolean"},
      ],
      searchOptions: {
        filters: [
          { field: "associated",  operator: FILTER_OPERATOR_ENUM.EQUAL.value,  variable: false },
          { field: "status",  operator: FILTER_OPERATOR_ENUM.EQUAL.value, variable: CLIENT_STATUS_ENUM.CONVERTED.value }
        ],
        sorts: [
          {field: "name", order: SORT_OPERATOR_ENUM.ASCENDING.value}
        ]
      }
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async removeAssociation(client){
      if(!confirm("Supprimer cette association ?"))
        return;

      this.$refs.table?.startLoading();

      const resp = await FrenchDirectoryCacheController.delete(client.id);

      this.$refs.table?.stopLoading();

      if(resp.errors)
        return;

      await this.loadData();
    },
    async loadData() {
      this.$refs.table?.startLoading();
      this.data = [];
      const resp = await FrenchDirectoryCacheController.clientDirectoryTable(this.$refs.table.buildURLParams());
      if (!resp.errors) {
        this.data.push(...resp);
      }
      this.$refs.table?.stopLoading();
    },
    setAssociationFilter(value) {
      const table = CustomTableService.data.get(this.$refs.table.tableId);

      table.currentFilters = table.currentFilters.filter(
          filter => filter.field !== "associated"
      );

      if (value !== null) {
        table.currentFilters.push({
          field: "associated",
          operator: FILTER_OPERATOR_ENUM.EQUAL.value,
          variable: value
        });
      }

      this.loadData();
    },
    isAssociationFilterActive(value) {
      const table = CustomTableService.data.get(this.$refs.table?.tableId);

      if (!table) return false;

      const filter = table.currentFilters?.find(
          filter => filter.field === "associated"
      );

      // Aucun filtre = "Tous"
      if (!filter) {
        return value === null;
      }

      return value !== null &&
          filter.operator === FILTER_OPERATOR_ENUM.EQUAL.value &&
          filter.variable === value;
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.button-icon
  width 40px
  height 40px
  margin-right 10px

.CONTACT
  background-color #FFD10036 !important
.QUALIFIED
  background-color #d9800036 !important
.INTERESTED
  background-color #ff4f0036 !important

.name
  display flex
  align-items center
  gap 10px
  .fa-circle-info
    height 20px
    width 20px
    color #499dbd

.directory
  display flex
  flex-direction column
  gap 2px
  min-width 0

// Texte trop long : tronqué par « … » (infobulle sur le titre) au lieu d'élargir le tableau
.truncate-cell, .directory > div
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.directory .title
  font-weight bold

.directory .subtitle
  color #777
  font-size 12px

.directory .identifier
  font-family monospace
  font-size 12px
  color #666

.associated .title
  color #2e7d32

.explicit-association .title
  color #526570

.not-associated
  color #e67e22
  font-weight bold

.empty
  text-align center
  font-weight bold
  background #40513b14
</style>
```
